import { HttpHeaders, HttpParams } from "@angular/common/http";
import { Injectable } from '@angular/core';
import { EmailSendDocumentRequest } from "@models/email-templates/email-bulk-send-request";
import { EmailModel } from "@models/email-templates/email-model";
import { EmailPreviewContentRequest, EmailPreviewRequest } from "@models/email-templates/email-preview-request";
import { EmailRecord } from "@models/email-templates/email-record";
import {
  IEmailPreviewContentResponse,
  IEmailPreviewResponse
} from "@models/email-templates/email-review-response.interface";
import { EmailSendRequest } from "@models/email-templates/email-send-request";
import { EmailTemplate } from "@models/email-templates/email-template";
import { TemplateSettingsBase } from "@models/email-templates/template-settings-base";
import { PageResult } from "@models/requests/page-result.interface";
import BaseApiService from "@shared/services/base/base-api.service";
import { IServerSideGetRowsRequest } from "ag-grid-enterprise";
import { map, Observable, of } from "rxjs";

@Injectable()
export class EmailsApiService extends BaseApiService {
  loadPreview(payload: EmailPreviewRequest): Observable<IEmailPreviewResponse> {
    const headers = new HttpHeaders();
    headers.append('Content-Type', 'multipart/form-data');
    return this.api.post<IEmailPreviewResponse>(`${ this.apiUrl }/emails/preview`, payload, { headers });
  }

  loadPreviewContent(payload: EmailPreviewContentRequest): Observable<IEmailPreviewContentResponse> {
    return this.api.post<IEmailPreviewContentResponse>(`${ this.apiUrl }/emails/preview/content`, payload);
  }

  loadEmailTemplates(): Observable<EmailTemplate[]> {
    return this.api.get<EmailTemplate[]>(`${ this.apiUrl }/emails/templates`);
  }

  loadEmailDefaultTemplates(): Observable<Record<string, TemplateSettingsBase>> {
    return this.api.get<Record<string, TemplateSettingsBase>>(`${ this.apiUrl }/emails/templates/default`);
  }

  saveEmailTemplate(emailTemplate: EmailTemplate): Observable<EmailTemplate> {
    return this.api.post<EmailTemplate>(`${ this.apiUrl }/emails/templates`, emailTemplate);
  }

  sendEmailTemplate(emailTemplate: EmailSendRequest): Observable<string> {
    return this.api.post<string>(`${ this.apiUrl }/emails/send`, emailTemplate);
  }

  sendDocument(emailTemplate: EmailSendDocumentRequest, documentId: string): Observable<string> {
    return this.api.post<string>(`${ this.apiUrl }/emails/send/document/${ documentId }`, emailTemplate);
  }

  getEmailList(): Observable<{ email: string, fullName: string }[]> {
    const emailList = [
      { email: 'luna.blackwood@innovatechsolutions1.com', fullName: 'Luna Blackwood1' },
      { email: 'luna.blackwood@innovatechsolutions2.com', fullName: 'Luna Blackwood2' },
      { email: 'luna.blackwood@innovatechsolutions3.com', fullName: 'Luna Blackwood3' }
    ];

    return of(emailList);
  }

  getOutgoingEmailMessagesList(request: IServerSideGetRowsRequest, search?: string): Observable<PageResult<EmailRecord>> {
    let params = new HttpParams();
    if (search) {
      params = params.append('search', search);
    }

    return this.api.post<PageResult<EmailRecord>>(`${ this.apiUrl }/emails/grid`, request, { params: params })
      .pipe(map((response) => {
        response.records = response.records.map((emailRecord) => new EmailRecord(emailRecord));
        return response;
      }));
  }

  getEmailDetails(emailMessageId: string): Observable<EmailModel> {
    return this.api.get(`${ this.apiUrl }/emails/${ emailMessageId }/details`)
      .pipe(map((emailModel) => new EmailModel(emailModel)));
  }

  downloadAttachment(emailMessageId: string, fileName: string): Observable<Blob> {
    return this.api.get(`${ this.apiUrl }/emails/${ emailMessageId }/attachments/${ fileName }`, { responseType: "blob" });
  }

  resendOutgoingEmailMessage(emailMessageId: string): Observable<string> {
    return this.api.get<string>(`${ this.apiUrl }/emails/${ emailMessageId }/resend`);
  }

  deleteOutgoingEmailMessage(emailMessageId: string): Observable<void> {
    return this.api.delete<void>(`${ this.apiUrl }/emails/${ emailMessageId }`);
  }
}
